<template>
  <div v-editable="blok" class="container">
    <div v-auto-animate class="grid grid-cols-4 gap-3 lg:gap-x-9 lg:gap-y-24">
      <template v-for="(card, cardIndex) in cards" :key="card._uid">
        <CardFeatured
          v-if="cardIndex % 5 === 4"
          :card="card"
          :image-position="cardIndex % 10 === 9 ? 'right' : 'left'"
          cta-label="Read more"
        />
        <CardSimple v-else :card="card" />
      </template>
    </div>

    <div
      v-if="showLoadMoreButton"
      class="flex justify-center pb-8 pt-8 lg:pt-14"
    >
      <UiButton
        theme="dark"
        label="Load more"
        variant="outline"
        size="md"
        @click="loadMore"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CardListCard } from '@/components/card/CardList.vue'
import type {
  BlockNewsListStoryblok,
  ContentTypeNewsArticleStoryblok,
} from '@/types/storyblok'
import { formatLink } from '@/utils/link-utils'
import type { FetchAPIOptions } from '@/composables/useStoryblokHelpers'
import { useThrottleFn } from '@vueuse/core'

const { fetchAsyncData } = useStoryblokHelpers()
const route = useRoute()
const props = defineProps<{ blok: BlockNewsListStoryblok }>()

const page = ref(1)
const cards = ref<CardListCard[]>([])
const defaultPerPage = 2

const perPage = computed(() =>
  props.blok.quantity ? parseInt(props.blok.quantity) : defaultPerPage,
)
const community = computed(() => props.blok.community || '')

const apiOptions = computed<FetchAPIOptions>(() => ({
  starts_with: useSlug().slugString.value,
  content_type: 'ContentTypeNewsArticle',
  sort_by: 'first_published_at:desc',
  per_page: perPage.value,
  page: page.value,
  excluding_fields: 'introduction,content',
}))

const { data, clear, refresh } =
  await fetchAsyncData<ContentTypeNewsArticleStoryblok>('stories', apiOptions, {
    watch: [page],
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    },
  })

// Note, we need to watch the data value to update the cards here,
// as we're keeping a growing list of cards as they are loaded
// we can't just compute them directly from the page of data loaded
watch(
  () => data.value?.data,
  () => {
    if (data.value?.data) {
      cards.value.push(
        ...data.value.data.stories.map(
          (article) =>
            ({
              _uid: article.uuid,
              title: article.name,
              description: article.content.shortDescription,
              image: article.content.featuredImage,
              link: formatLink(article.full_slug),
            }) as CardListCard,
        ),
      )
    }
  },
  { immediate: true },
)

const total = computed(() => data.value?.total || 0)

const showLoadMoreButton = computed(
  () => !total?.value || cards.value.length < total.value,
)

const loadMore = useThrottleFn(() => {
  page.value += 1
}, 200)

function reset() {
  page.value = 1
  cards.value = []
  clear()
  refresh()
}

watch([perPage, community], reset)
watch(route, reset, { immediate: true })
</script>
